// Tenant profile read + update (PRD §4.9). Business name / timezone / default
// language live on the tenant doc; the save goes through tenantUpdateProfile
// (owner/admin guarded server-side).
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { stripUndefined } from './callable';
import { useTenant } from './tenant';

export interface TenantProfile {
  businessName: string;
  timezone: string;
  defaultLang: 'en' | 'ar';
}

export function useTenantProfile(): { profile: TenantProfile | null; loading: boolean } {
  const { tenantId } = useTenant();
  const [profile, setProfile] = useState<TenantProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) { setProfile(null); setLoading(false); return; }
    const ref = doc(db, `tenants/${tenantId}`);
    return onSnapshot(
      ref,
      (snap) => {
        const d = snap.data() as { name?: string; timezone?: string; defaultLang?: 'en' | 'ar' } | undefined;
        setProfile({
          businessName: d?.name ?? '',
          timezone: d?.timezone ?? 'Asia/Muscat (GMT+4)',
          defaultLang: d?.defaultLang ?? 'en',
        });
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [tenantId]);

  return { profile, loading };
}

const updateProfileFn = httpsCallable<Partial<TenantProfile>, { ok: boolean }>(functions, 'tenantUpdateProfile');

export function updateTenantProfile(patch: Partial<TenantProfile>): Promise<{ ok: boolean }> {
  return updateProfileFn(stripUndefined(patch)).then((r) => r.data);
}

// ── quiet hours ──────────────────────────────────────────────────────────────
export interface QuietHours {
  enabled: boolean;
  start: string;
  end: string;
  utcOffset: number;
  fridayPrayer: boolean;
  ramadan: boolean;
}

export function useQuietHours(): { quiet: QuietHours | null; loading: boolean } {
  const { tenantId } = useTenant();
  const [quiet, setQuiet] = useState<QuietHours | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) { setQuiet(null); setLoading(false); return; }
    const ref = doc(db, `tenants/${tenantId}/config/quietHours`);
    return onSnapshot(
      ref,
      (snap) => {
        const d = snap.data() as
          | { enabled?: boolean; default?: { start?: string; end?: string }; utcOffset?: number; fridayPrayer?: boolean; ramadan?: boolean }
          | undefined;
        setQuiet({
          enabled: d?.enabled ?? true,
          start: d?.default?.start ?? '21:00',
          end: d?.default?.end ?? '09:00',
          utcOffset: d?.utcOffset ?? 4,
          fridayPrayer: d?.fridayPrayer ?? true,
          ramadan: d?.ramadan ?? true,
        });
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [tenantId]);

  return { quiet, loading };
}

const updateQuietFn = httpsCallable<QuietHours, { ok: boolean }>(functions, 'tenantUpdateQuietHours');

export function updateQuietHours(next: QuietHours): Promise<{ ok: boolean }> {
  return updateQuietFn(next).then((r) => r.data);
}
